/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import {
  FiGlobe,
  FiClock,
  FiWifi,
  FiPhoneCall,
  FiSmartphone,
  FiX,
  FiArrowRight,
} from 'react-icons/fi';
import {
  formatPrice,
  formatDataCapacity,
  formatValidity,
  parseCoverages,
  derivePlanTitle,
  derivePlanSubtitle,
  stripPlanDescription,
} from '../../utils/esimUtils';
import './EsimPlanDetailsModal.scss';

function Stat({ icon, label, value }) {
  return (
    <div className="esim-plan-details__stat">
      <span className="esim-plan-details__stat-icon">{icon}</span>
      <div>
        <span className="esim-plan-details__stat-label">{label}</span>
        <strong className="esim-plan-details__stat-value">{value}</strong>
      </div>
    </div>
  );
}

Stat.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

function EsimPlanDetailsModal({ show, plan, onHide, onSelect }) {
  if (!plan) return null;
  const title = derivePlanTitle(plan);
  const subtitle = derivePlanSubtitle(plan);
  const coverages = parseCoverages(plan?.coverages);
  const description = stripPlanDescription(plan?.description);
  const currency = plan?.currency || 'INR';
  const total = Number(plan?.totalPrice || plan?.netPrice || 0);

  const calling = (() => {
    const local = Number(plan?.localCallingCapacity || 0);
    const intl = Number(plan?.internationalCallingCapacity || 0);
    if (local <= 0 && intl <= 0) return 'Data only (no calls)';
    const parts = [];
    if (local > 0)
      parts.push(`Local ${local} ${plan?.localCallingCapacityUnit || 'min'}`);
    if (intl > 0)
      parts.push(
        `Intl ${intl} ${plan?.internationalCallingCapacityUnit || 'min'}`,
      );
    return parts.join(' · ');
  })();

  const sms = Number(plan?.smsCapacity || 0);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      dialogClassName="esim-plan-details"
    >
      <Modal.Body>
        <button
          type="button"
          className="esim-plan-details__close"
          onClick={onHide}
          aria-label="Close"
        >
          <FiX />
        </button>

        <header className="esim-plan-details__head">
          <div>
            <h2 className="esim-plan-details__title">{title}</h2>
            {subtitle && (
              <p className="esim-plan-details__subtitle">{subtitle}</p>
            )}
          </div>
          <div className="esim-plan-details__price">
            <span>
              {formatPrice(total, currency) || `${currency} ${total}`}
            </span>
            <small>incl. taxes</small>
          </div>
        </header>

        <div className="esim-plan-details__stats">
          <Stat
            icon={<FiWifi />}
            label="Data"
            value={formatDataCapacity(plan)}
          />
          <Stat
            icon={<FiClock />}
            label="Validity"
            value={formatValidity(plan) || 'Flexible'}
          />
          <Stat icon={<FiPhoneCall />} label="Calling" value={calling} />
          <Stat
            icon={<FiSmartphone />}
            label="SMS"
            value={sms > 0 ? `${sms} SMS` : 'Not included'}
          />
        </div>

        <section className="esim-plan-details__section">
          <h3>
            <FiGlobe /> Countries Covered ({coverages.length || 1})
          </h3>
          <div className="esim-plan-details__coverages">
            {coverages.length === 0 && (
              <span>{plan?.country_covered || '—'}</span>
            )}
            {coverages.map((c) => (
              <span key={c} className="esim-plan-details__coverage">
                {c}
              </span>
            ))}
          </div>
        </section>

        {description && (
          <section className="esim-plan-details__section">
            <h3>About this plan</h3>
            <pre className="esim-plan-details__desc">{description}</pre>
          </section>
        )}
      </Modal.Body>
      <Modal.Footer className="esim-plan-details__footer">
        <button
          type="button"
          className="esim-plan-details__cancel"
          onClick={onHide}
        >
          Close
        </button>
        <button
          type="button"
          className="esim-plan-details__confirm"
          onClick={() => {
            if (onSelect) onSelect(plan);
            onHide();
          }}
        >
          Select this plan
          <FiArrowRight />
        </button>
      </Modal.Footer>
    </Modal>
  );
}

EsimPlanDetailsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  plan: PropTypes.object,
  onHide: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
};

EsimPlanDetailsModal.defaultProps = {
  plan: null,
  onSelect: null,
};

export default EsimPlanDetailsModal;
